import React from "react";
import { defineMessage, useIntl } from "react-intl";

import { SearchableEntity } from "@/components/common/Search/types";
import { IconButton } from "@/components/ui/IconButton";
import { Spinner } from "@/components/ui/Spinner";
import { Close20, Search24 } from "@/components/ui/SvgIcon";
import { Tooltip } from "@/components/ui/Tooltip";

import cn from "@/utils/cn";
import { UiUtils } from "@/utils/ui";

type Props = React.HTMLAttributes<HTMLDivElement> & {
  query?: string;
  setQuery?: React.Dispatch<React.SetStateAction<string>>;
  isLoading?: boolean;
  entity?: SearchableEntity;
  onFocus?: () => void;
};

export const SearchInput = ({
  query,
  setQuery,
  isLoading,
  entity,
  onFocus,
  className = "",
}: Props) => {
  const intl = useIntl();
  React.useEffect(() => {
    UiUtils.focusElement("#search", "/");
  });

  return (
    <div className="relative w-full flex-1 md:min-w-[203px]">
      <Search24
        data-testid="search-input-icon"
        className="pointer-events-none absolute left-4 top-3 fill-chorus-gray-500 peer-focus:fill-chorus-gray-600"
      />
      <input
        value={query}
        onChange={(e) => setQuery && setQuery(e.target.value)}
        onFocus={onFocus}
        className={cn(
          "peer block h-12 w-full flex-1 overflow-hidden rounded-l-full border-0 bg-chorus-gray-100 pl-[46px] pr-9 font-sora text-[14px] font-superNormal leading-none text-chorus-black-200 caret-chorus-gray-700 placeholder:text-chorus-gray-500 focus:border-white focus:outline-none focus:ring-2 focus:ring-transparent md:bg-white",
          className,
        )}
        data-testid="search-input"
        id={"search"}
        placeholder={intl.formatMessage(
          entity?.placeholder ||
            defineMessage({
              defaultMessage: "Search for in...",
              description:
                "Generic placeholder text shown in global search input field before user selects a specific entity type (assets, trips, etc) to search within",
            }),
        )}
        autoComplete="off"
      />
      {isLoading ? (
        <div
          data-testid="search-input-loading-icon"
          className="absolute right-4 top-1/2 flex -translate-y-1/2"
        >
          <Spinner size="xxs" className="!text-chorus-teal-500" />
        </div>
      ) : query ? (
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2">
          <IconButton
            data-testid="search-input-clear-icon"
            icon={<Close20 className="size-4" />}
            size="xxxs"
            variant="link"
            className="rounded-full md:bg-chorus-gray-100"
            onClick={() => setQuery && setQuery("")}
          />
        </div>
      ) : null}
      {!query && (
        <div className="absolute right-[14px] top-1/2 -translate-y-1/2 max-md:hidden">
          <div className="absolute right-[14px] top-1/2 -translate-y-1/2">
            <Tooltip
              content="Use “/” key to trigger search"
              placement={"bottom-end"}
              className="w-[180px]"
            >
              <button
                data-testid="search-input-hotkey-icon"
                className="size-5 rounded bg-chorus-gray-100 font-sora text-xxs text-chorus-gray-700"
              >
                /
              </button>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};
